import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { SessionService } from '../session.service';
import { StorageService } from 'src/app/services/storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  private auth = inject(SessionService);
  private router = inject(Router);
  private storage = inject(StorageService);

  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // Wait for auth state to be resolved (handles Firebase async token restore on page load)
    // If already resolved, check synchronously to avoid hanging the guard
    if (this.auth.isAuthResolved$.getValue()) {
      if (!this.auth.isAuthenticated$.getValue()) {
        return this.router.createUrlTree(['login'], { queryParams: { returnUrl: state.url ?? 'home' } });
      }
      return this.identitySetupRedirect(route, state);
    }

    return this.auth.isAuthResolved$.pipe(
      filter((resolved) => resolved),
      take(1),
      map(() => {
        if (!this.auth.isAuthenticated$.getValue()) {
          return this.router.createUrlTree(['login'], { queryParams: { returnUrl: state.url ?? 'home' } });
        }
        return this.identitySetupRedirect(route, state);
      })
    );
  }

  private identitySetupRedirect(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): true | UrlTree {
    if (route.routeConfig?.path === 'identity/setup') {
      return true;
    }
    const raw = this.storage.getValue('store-userObj');
    if (!raw) {
      return true;
    }
    try {
      const user = JSON.parse(raw) as { profileSetupRequired?: boolean };
      if (user.profileSetupRequired) {
        sessionStorage.setItem('hb-identity-return-url', state.url ?? '/home');
        return this.router.createUrlTree(['/identity/setup']);
      }
    } catch {
      return true;
    }
    return true;
  }
}
